<template>
  <div class="md:px-5 md:pt-8 mx-auto w-full bg-bgnew min-h-[calc(100vh-80px)]">
    <!-- фон страницы -->
    <div
      v-if="showCaseData.cover"
      class="fixed top-0 left-0 w-full h-full z-0 pointer-events-none overflow-hidden"
    >
      <NuxtImg :src="showCaseData.cover" alt="" class="w-full h-full object-cover" />
      <div class="absolute inset-0 backdrop-blur-md"></div>
    </div>

    <div v-if="isLoading" class="relative z-[2] flex items-center justify-center p-8">
      <div class="animate-spin rounded-full size-16 border-b-2 border-primary"></div>
    </div>

    <!-- витрина отключена -->
    <div v-else-if="isShowcaseInactive" class="relative z-[2] flex items-center justify-center p-8">
      <div class="bg-white rounded-[32px] max-w-[800px] w-full mx-auto py-12 px-8 flex flex-col items-center">
        <NuxtImg src="/assets/svg/404.svg" alt="Витрина отключена" class="w-[220px] mb-8" />
        <h2 class="text-txprimary text-[28px] font-bold text-center leading-[1.3] mb-4">
          Витрина отключена автором
        </h2>
        <p class="text-txprimary text-[16px] text-center leading-[1.5] mb-8 max-w-[409px]">
          УПС, автор скрыл витрину от просмотра :(
        </p>
        <button
          class="bg-primary text-white text-[16px] font-bold rounded-[20px] h-[70px] w-full max-w-[376px] hover:opacity-90 transition-opacity"
          @click="navigateTo('/')"
        >
          Смотреть другие витрины?
        </button>
      </div>
    </div>

    <div v-else class="relative z-[2] text-txprimary py-4 md:py-6 px-3 md:px-0">
      <div class="max-w-[971px] mx-auto flex flex-col gap-5">
        <!-- ============ HERO ============ -->
        <div class="bg-white rounded-[24px]">
          <!-- баннер: обложка или сильно размытый аватар -->
          <div class="relative">
            <!-- клип-контейнер картинки: скругляем верхние углы, обрезаем картинку -->
            <div
              class="relative h-[120px] md:h-[200px] w-full bg-[#f1f4f9] overflow-hidden rounded-t-[24px]"
            >
              <img
                v-if="hasBanner"
                :src="showCaseData.backgroundColor"
                alt=""
                class="w-full h-full object-cover"
              />
              <template v-else-if="showCaseData.avatar">
                <img
                  :src="showCaseData.avatar"
                  alt=""
                  class="w-full h-full object-cover blur-2xl scale-125"
                />
                <div class="avatar-grain absolute inset-0 pointer-events-none"></div>
              </template>
            </div>

            <!-- аватар внахлёст на нижнюю кромку баннера (сиблинг картинки — не обрезается) -->
            <div
              v-if="showAvatar"
              class="absolute left-4 md:left-8 -bottom-8 md:-bottom-10 size-16 md:size-20 rounded-full border-2 border-white bg-[#f1f4f9] bg-cover bg-center shadow-[0px_3px_12px_0px_rgba(49,118,255,0.14)]"
              :style="showCaseData.avatar ? { backgroundImage: `url(${showCaseData.avatar})` } : {}"
            ></div>
          </div>

          <!-- контент -->
          <div class="px-4 md:px-8 pb-6 md:pb-8">
            <div class="flex gap-6 max-md:flex-col md:items-start pt-11 md:pt-[52px]">
              <!-- слева: заголовок, описание, статы -->
              <div class="flex-1 min-w-0">
                <h1 class="text-[24px] md:text-[32px] font-black leading-[1.25] text-txprimary truncate">
                  {{ showCaseData.name }}
                </h1>
                <div
                  v-if="!isRichTextEmpty(showCaseData.description)"
                  class="mt-2 text-[16px] text-txopacity leading-[1.5] break-words showcase-description"
                  v-html="renderRichText(showCaseData.description)"
                ></div>

                <div class="flex flex-wrap gap-2 mt-6">
                  <div class="bg-[#f1f4f9] rounded-[16px] px-3 py-2 text-[14px] whitespace-nowrap">
                    <span class="font-black text-txprimary">{{ activeChannels.length }}</span>
                    <span class="text-txprimary">{{ getDeclension(activeChannels.length, "канал") }}</span>
                  </div>
                  <div class="bg-[#f1f4f9] rounded-[16px] px-3 py-2 text-[14px] whitespace-nowrap">
                    <span class="font-black text-txprimary">{{ audience }}</span>
                    <span class="text-txprimary"> аудитория</span>
                  </div>
                  <div class="bg-[#f1f4f9] rounded-[16px] px-3 py-2 text-[14px] whitespace-nowrap">
                    <span class="font-black text-txprimary">{{ minPlacement }}</span>
                    <span class="text-[12px] text-txprimary"> размещение</span>
                  </div>
                </div>
              </div>

              <!-- справа: контакты (если автор указал) -->
              <div
                v-if="visibleContacts.length"
                class="md:w-[378px] shrink-0 bg-[#f9fafc] rounded-[24px] p-5"
              >
                <div class="text-[16px] font-bold text-txprimary">Контакты для связи</div>
                <div class="mt-4 flex flex-col gap-3">
                  <div
                    v-for="contact in visibleContacts"
                    :key="contact.type"
                    class="flex items-center gap-3"
                  >
                    <Icon
                      :name="CONTACT_ICONS[contact.type] || 'mdi:link-variant'"
                      class="text-xl shrink-0"
                      :style="{ color: mainColor }"
                    />
                    <!-- Ссылки на контакты задаёт автор витрины: наружу вес
                         не передаём и переход не отслеживаем -->
                    <a
                      :href="contactHref(contact)"
                      :target="['email', 'phone'].includes(contact.type) ? undefined : '_blank'"
                      rel="nofollow noindex noopener noreferrer"
                      class="text-[14px] text-txprimary leading-5 break-all hover:underline"
                    >
                      {{ contactLabel(contact) }}
                    </a>
                  </div>
                </div>
                <p class="mt-4 text-[14px] text-[#a5aeb7] leading-[1.4]">
                  Для быстрой связи по спецпроектам и нестандартным форматам
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- ============ TABS ============ -->
        <div v-if="availablePlatforms.length > 1" class="flex items-center gap-2">
          <button
            v-for="tab in availablePlatforms"
            :key="tab.value"
            class="h-10 px-4 rounded-[12px] text-[12px] font-medium bg-white transition-colors flex-1 md:flex-none md:min-w-[81px]"
            :class="selectedPlatform === tab.value ? 'text-white' : 'text-txprimary hover:bg-bgnew'"
            :style="selectedPlatform === tab.value ? { backgroundColor: mainColor } : {}"
            @click="selectedPlatform = tab.value"
          >
            {{ tab.label }}
          </button>
        </div>

        <!-- ============ CHANNELS ============ -->
        <div v-if="filteredChannels.length" class="grid grid-cols-1 md:grid-cols-2 gap-3.5">
          <PublisherShowcasePreviewChannelCard
            v-for="channel in filteredChannels"
            :key="channel.place.id"
            :channel="channel"
            :color="mainColor"
          >
            <template #actions>
              <button
                class="w-full h-10 rounded-[12px] flex items-center justify-center text-white text-[14px] transition-opacity hover:opacity-90"
                :style="{ backgroundColor: mainColor }"
                @click="openFormatModal(channel.place)"
              >
                Купить рекламу
              </button>
              <button
                class="w-full h-10 rounded-[12px] flex items-center justify-center border border-[#d9e5ff] bg-white text-[14px] transition-colors hover:bg-bgnew"
                :style="{ color: mainColor }"
                @click="openChannelPage(channel.place)"
              >
                Подробнее
              </button>
            </template>
          </PublisherShowcasePreviewChannelCard>
        </div>
        <div
          v-else
          class="bg-white rounded-[20px] py-14 px-4 flex flex-col items-center text-center"
        >
          <div class="text-[18px] md:text-[20px] font-bold">На витрине пока нет каналов</div>
        </div>

        <!-- ============ TRUST BADGES ============ -->
        <div class="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4 mt-3">
          <div
            v-for="badge in trustBadges"
            :key="badge.title"
            class="bg-white rounded-[24px] px-5 py-4"
          >
            <div class="text-[14px] font-medium text-txprimary leading-5">{{ badge.title }}</div>
            <p class="mt-1 text-[12px] text-txopacity leading-[1.3]">{{ badge.text }}</p>
          </div>
        </div>

        <!-- ============ FOOTER ============ -->
        <div
          class="flex flex-col md:flex-row items-center justify-center gap-2 md:gap-6 mt-8 pb-4 text-[12px] leading-[1.3]"
        >
          <span class="font-medium" :class="showCaseData.cover ? 'text-white' : 'text-txprimary'">
            telepost
          </span>
          <NuxtLink
            to="/documents"
            target="_blank"
            rel="nofollow noindex noopener noreferrer"
            class="!w-fit whitespace-nowrap hover:opacity-70 transition-opacity"
            :class="showCaseData.cover ? '!text-white/80' : '!text-txopacity'"
          >
            Юридические документы
          </NuxtLink>
          <a
            href="https://t.me/suptelepost"
            target="_blank"
            rel="nofollow noindex noopener noreferrer"
            class="!w-fit whitespace-nowrap hover:opacity-70 transition-opacity"
            :class="showCaseData.cover ? '!text-white/80' : '!text-txopacity'"
          >
            Поддержка @suptelepost
          </a>
        </div>
      </div>
    </div>
  </div>

  <!-- модалка выбора формата -->
  <PublisherShowcaseFormatSelectModal
    v-model:open="isFormatModalOpen"
    :place="formatModalPlace"
    :color="mainColor"
    @add="addPlaceToBasket"
  />

  <!-- корзина -->
  <PublisherShowcaseCartDrawer v-model:open="isCartOpen" :color="mainColor" />
</template>

<script lang="ts" setup>
  import { useMyPublisherShowcaseStore } from "~/store/PublisherShowcaseModule";
  import { renderRichText, isRichTextEmpty } from "~/common/richText";
  import { getDeclension } from "~/helpers/UI";
  import { useBasketStore } from "~/store/BasketModule";
  import { usePlaceModuleStore } from "~/store/PlaceModule";
  import BasketService from "~/services/BasketService";
  import type { PlacePrice, ReducedPlace } from "~/interfaces/publisher";

  definePageMeta({
    layout: "data",
  });

  const store = useMyPublisherShowcaseStore();
  const placeModuleStore = usePlaceModuleStore();
  const { colorOptions } = storeToRefs(placeModuleStore);

  const route = useRoute();

  const showCaseData = ref<any>({
    slug: "",
    name: "",
    description: "",
    channels: [],
    avatar: "",
    cover: "",
    isActive: false,
    contacts: [],
  });

  // ---- contacts ----
  const CONTACT_LABELS: Record<string, string> = {
    telegram: "Telegram",
    vk: "ВКонтакте",
    max: "MAX",
    email: "Почта",
    site: "Сайт",
    phone: "Телефон",
  };
  const CONTACT_ICONS: Record<string, string> = {
    telegram: "mdi:telegram",
    vk: "mdi:vk",
    max: "mdi:chat",
    email: "mynaui:mail",
    site: "mdi:web",
    phone: "mdi:phone",
  };
  const visibleContacts = computed(() =>
    (showCaseData.value.contacts || []).filter((c: any) => c.isEnabled && c.value)
  );

  // Кликабельные контакты: email → mailto, телефон → tel, остальные — ссылка.
  const contactHref = (contact: any): string => {
    const value = String(contact.value || "").trim();
    if (contact.type === "email") return `mailto:${value}`;
    if (contact.type === "phone") return `tel:${value.replace(/[^\d+]/g, "")}`;
    return /^https?:\/\//i.test(value) ? value : `https://${value}`;
  };

  // В тексте ссылок протокол не показываем.
  const contactLabel = (contact: any): string =>
    String(contact.value || "").trim().replace(/^https?:\/\//i, "");

  // Показывать ли баннер-обложку. Если она загружена — показываем её,
  // иначе в баннере рисуем размытый аватар.
  const hasBanner = computed(() => !!showCaseData.value.backgroundColor);
  const showAvatar = computed(() => showCaseData.value.showAvatar !== false);

  // === SEO ===
  // Данные витрины грузятся в onMounted (только на клиенте), а страница рендерится с SSR,
  // поэтому для мета-тегов запрашиваем название отдельно — на сервере.
  // simpleApi здесь не подходит: он читает localStorage и на SSR падает.
  const { data: seoShowcase } = await useAsyncData(
    `showcase-seo-${route.params.url}`,
    async () => {
      try {
        const config = useRuntimeConfig();
        return await $fetch<any>(`${config.public.LIGHT_API_ORIGIN}/user/showcase/`, {
          params: { slug: route.params.url },
          headers: { "Auth-Type": "asPublisher" },
        });
      } catch {
        return null;
      }
    }
  );

  // Название витрины: на сервере из seoShowcase, на клиенте — из загруженных данных.
  const seoName = computed(() => showCaseData.value?.name || seoShowcase.value?.name || "");

  // Тематика витрины — уникальные категории её каналов, в порядке появления.
  // Ограничиваем список, чтобы description не разрастался: поисковики обрезают длинные.
  const SEO_MAX_CATEGORIES = 5;
  const seoCategories = computed<string[]>(() => {
    const channels = showCaseData.value?.channels?.length
      ? showCaseData.value.channels
      : seoShowcase.value?.showcasePlaces || [];
    const names: string[] = [];
    channels.forEach((channel: any) => {
      (channel?.place?.categories || []).forEach((category: any) => {
        const name = category?.name?.trim();
        if (name && !names.includes(name)) names.push(name);
      });
    });
    return names.slice(0, SEO_MAX_CATEGORIES);
  });

  const seoTitle = computed(() =>
    seoName.value
      ? `${seoName.value} | Реклама в каналах на Telepost.pro`
      : "Реклама в каналах на Telepost.pro"
  );

  const seoDescription = computed(() => {
    const name = seoName.value;
    const topics = seoCategories.value.length ? ` по теме ${seoCategories.value.join(", ")}` : "";
    return name
      ? `${name} тематическая подборка каналов на Telepost. Размещайте рекламу в Telegram, Max, ВК${topics}.`
      : `Тематическая подборка каналов на Telepost. Размещайте рекламу в Telegram, Max, ВК${topics}.`;
  });

  // Домен берём из запроса: в BASE_URL на проде записан внутренний хост,
  // а канонический адрес должен быть на telepost.pro
  const requestUrl = useRequestURL();
  const canonicalUrl = computed(() => `${requestUrl.origin}/s/${route.params.url}`);

  useSeoMeta({
    title: seoTitle,
    description: seoDescription,
    ogTitle: seoTitle,
    ogDescription: seoDescription,
    ogType: "website",
    ogUrl: canonicalUrl,
    twitterCard: "summary_large_image",
    twitterTitle: seoTitle,
    twitterDescription: seoDescription,
  });

  useHead(() => ({
    link: [{ rel: "canonical", href: canonicalUrl.value }],
  }));

  const isLoading = ref(true);
  const isShowcaseInactive = ref(false);

  const mainColor = ref<string>(colorOptions.value[0] || "#3176FF");
  const setMainColor = (colorIndex: number) => {
    mainColor.value = colorOptions.value[colorIndex] || "#3176FF";
  };

  // ---- platforms ----
  const selectedPlatform = ref<"telegram" | "vk" | "max">("telegram");

  const getSocialFromUrl = (url: string) => {
    const u = String(url || "").toLowerCase();
    if (u.includes("vk.com") || u.includes("vk.ru")) return "vk";
    if (u.includes("max.ru") || u.includes("my.mail.ru")) return "max";
    return "telegram";
  };

  const channelSocial = (item: any) =>
    item.place?.social || getSocialFromUrl(item.place?.url || "");

  const activeChannels = computed(() =>
    (showCaseData.value.channels || []).filter(
      (item: any) => item.isActive !== false && item.place?.isActive !== false
    )
  );

  const PLATFORM_LABELS: Record<string, string> = {
    telegram: "Telegram",
    vk: "ВКонтакте",
    max: "MAX",
  };

  const availablePlatforms = computed(() => {
    const socials = new Set<string>(activeChannels.value.map((item: any) => channelSocial(item)));
    return ["telegram", "vk", "max"]
      .filter((s) => socials.has(s))
      .map((s) => ({ value: s as "telegram" | "vk" | "max", label: PLATFORM_LABELS[s] }));
  });

  watch(
    availablePlatforms,
    (platforms) => {
      if (platforms.length && !platforms.some((p) => p.value === selectedPlatform.value)) {
        selectedPlatform.value = platforms[0].value;
      }
    },
    { immediate: true }
  );

  const filteredChannels = computed(() =>
    activeChannels.value.filter((item: any) => channelSocial(item) === selectedPlatform.value)
  );

  // ---- hero stats ----
  const formatNum = (n: number): string => {
    if (!n) return "0";
    if (n >= 1000) return `${(n / 1000).toFixed(1).replace(".0", "")}к`;
    return n.toLocaleString();
  };

  const audience = computed(() => {
    const total = activeChannels.value.reduce(
      (sum: number, c: any) => sum + (c.place?.subscribersStatistic?.count || 0),
      0
    );
    return total ? formatNum(total) : "-";
  });

  const minPlacement = computed(() => {
    let min = Infinity;
    activeChannels.value.forEach((c: any) => {
      const prices = c.place?.prices || c.place?.costs || [];
      prices.forEach((p: any) => {
        if (p.price > 0 && p.price < min) min = p.price;
      });
    });
    return min === Infinity ? "-" : `от ${min.toLocaleString()} ₽`;
  });

  const trustBadges = [
    {
      title: "Безопасная сделка",
      text: "Деньги перечисляются автору только после выполнения заказа",
    },
    { title: "Маркировка постов", text: "Автоматическая отправка данных и отчетность в ОРД." },
    { title: "Поддержка", text: "По любому заказу и вопросу вы можете обратиться в поддержку." },
  ];

  // ---- basket ----
  const basketStore = useBasketStore();
  const { basket } = storeToRefs(basketStore);

  const isCartOpen = ref(false);

  const getBasket = () => {
    const basketData = BasketService.getBasket();
    if (basketData?.places) {
      basketData.places.forEach((place: any) => {
        if (!("quantity" in place)) place.quantity = 1;
        if (!("format" in place)) place.format = null;
      });
      basket.value = basketData.places;
    }
  };

  const addPlaceToBasket = (place: ReducedPlace, price: PlacePrice) => {
    const existing = basket.value.find(
      (item: any) => item.id === place.id && item.format?.priceType === price.priceType
    );
    if (existing) {
      existing.quantity = (existing.quantity || 1) + 1;
    } else {
      // независимая копия без циклических ссылок
      const basketItem = JSON.parse(
        JSON.stringify({
          id: place.id,
          name: place.name,
          url: place.url,
          avatar: place.avatar,
          format: price,
          quantity: 1,
          categories: place.categories,
          subscribersStatistic: place.subscribersStatistic,
          prices: place.prices,
          viewsStatistic: place.viewsStatistic,
          er: place.er,
        })
      );
      basket.value.push(basketItem);
    }
    isCartOpen.value = true;
  };

  // ---- modals ----
  const isFormatModalOpen = ref(false);
  const formatModalPlace = ref<any>(null);
  const openFormatModal = (place: any) => {
    formatModalPlace.value = place;
    isFormatModalOpen.value = true;
  };

  // «Подробнее» — переход на страницу канала (в новой вкладке, чтобы витрина осталась открытой)
  const openChannelPage = (place: any) => {
    const id = place?.slug || place?.id;
    if (!id) return;
    navigateTo(`/ch/${id}`, { open: { target: "_blank" } });
  };

  // ---- data ----
  const getShowcase = async () => {
    const response = await store.getShowCaseDataPublic(route.params.url as string);
    if (!response || !response?.isActive) {
      isShowcaseInactive.value = true;
      isLoading.value = false;
      return;
    }
    showCaseData.value = {
      ...response,
      channels: response.showcasePlaces,
    };
    setMainColor(response.buttonsColor);
    isLoading.value = false;
  };

  onMounted(async () => {
    await getShowcase();
    getBasket();
  });
</script>
